import React, { useEffect, useMemo, useState } from 'react';
import { X, Link2, Plus, Trash2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getAttributesRequest,
  selectAttributes,
} from '../../../../store/attributes';
import {
  getEventAttributesRequest,
  selectEventAttributes,
} from '../../../../store/eventAttributes';
import {
  getCampaignRequest,
  selectSelectedCampaign,
} from '../../../../store/campaigns';
import { COMMON_FIELDS } from '../../../../constants/segmentConstants';
import MergeFieldInput, { MergeFieldOption } from './MergeFieldInput';
import Button from '../../../ui/Button';
import Select from '../../../ui/Select';

type HeaderItem = {
  name: string;
  value: string;
};

type KeyValueItem = {
  key: string;
  value: string;
};

type PayloadObject = Record<string, string>;
interface WebhookPanelProps {
  onClose: () => void;
  onSave: (webhookData: {
    webhookUrl: string;
    method: string;
    headers: HeaderItem[];
    payload: PayloadObject;
  }) => void;
  initialWebhookUrl?: string;
  initialMethod?: string;
  initialHeaders?: HeaderItem[];
  initialPayload?: KeyValueItem[] | PayloadObject;
}

const EMPTY_PAIR: KeyValueItem = { key: '', value: '' };
const EMPTY_HEADER_PAIR: HeaderItem = { name: '', value: '' };
const DEFAULT_HEADER: HeaderItem = {
  name: 'content-type',
  value: 'application/json',
};

const isDefaultContentTypeHeader = (item: HeaderItem) =>
  item.name.trim().toLowerCase() === DEFAULT_HEADER.name &&
  item.value.trim().toLowerCase() === DEFAULT_HEADER.value;

const normalizeHeaderPairs = (pairs?: HeaderItem[]) => {
  if (!pairs || pairs.length === 0) return [EMPTY_HEADER_PAIR];
  return pairs.map((pair) => ({
    name: (pair as HeaderItem)?.name || (pair as any)?.key || '',
    value: pair?.value || '',
  }));
};

const normalizePairs = (pairs?: KeyValueItem[] | PayloadObject) => {
  if (!pairs) return [EMPTY_PAIR];
  if (!Array.isArray(pairs)) {
    const objectPairs = Object.entries(pairs).map(([key, value]) => ({
      key,
      value: String(value ?? ''),
    }));
    return objectPairs.length > 0 ? objectPairs : [EMPTY_PAIR];
  }
  if (pairs.length === 0) return [EMPTY_PAIR];
  return pairs.map((pair) => ({
    key: pair?.key || '',
    value: pair?.value || '',
  }));
};

const isValidWebhookUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 2048) return false;
  if (!/^https:\/\//i.test(trimmed)) return false;

  try {
    const urlWithPlaceholdersResolved = trimmed.replace(
      /\{\{\s*[^{}]+\s*\}\}/g,
      'merge-field'
    );
    const parsed = new URL(urlWithPlaceholdersResolved);

    if (parsed.protocol !== 'https:') return false;

    return true;
  } catch {
    return false;
  }
};

const WebhookPanel: React.FC<WebhookPanelProps> = ({
  onClose,
  onSave,
  initialWebhookUrl = '',
  initialMethod = 'POST',
  initialHeaders = [],
  initialPayload = [],
}) => {
  const dispatch = useDispatch<any>();
  const { campaignId } = useParams<{ campaignId: string }>();
  const attributes = useSelector(selectAttributes) as any;
  const eventAttributes = useSelector(selectEventAttributes) as any;
  const campaign = useSelector(selectSelectedCampaign);
  const [webhookUrl, setWebhookUrl] = useState(initialWebhookUrl);
  const [method, setMethod] = useState(initialMethod);
  const [headers, setHeaders] = useState<HeaderItem[]>(
    normalizeHeaderPairs(initialHeaders)
  );
  const [payload, setPayload] = useState<KeyValueItem[]>(
    normalizePairs(initialPayload)
  );
  const [urlTouched, setUrlTouched] = useState(false);

  const hasChanges = useMemo(() => {
    return (
      webhookUrl !== initialWebhookUrl ||
      method !== initialMethod ||
      JSON.stringify(headers) !==
        JSON.stringify(normalizeHeaderPairs(initialHeaders)) ||
      JSON.stringify(payload) !== JSON.stringify(normalizePairs(initialPayload))
    );
  }, [
    headers,
    initialHeaders,
    initialMethod,
    initialPayload,
    initialWebhookUrl,
    method,
    payload,
    webhookUrl,
  ]);

  const urlIsValid = useMemo(() => isValidWebhookUrl(webhookUrl), [webhookUrl]);
  const showUrlError = urlTouched && !urlIsValid;
  const trimmedWebhookUrl = webhookUrl.trim();
  const supportsPayload = ['POST', 'PUT', 'PATCH'].includes(method);

  useEffect(() => {
    dispatch(getAttributesRequest());
  }, [dispatch]);

  useEffect(() => {
    if (campaignId) {
      dispatch(getCampaignRequest(campaignId));
    }
  }, [campaignId, dispatch]);

  useEffect(() => {
    if (campaign?.triggerType === 'event' && campaign?.event) {
      dispatch(getEventAttributesRequest(campaign.event));
    }
  }, [campaign?.event, campaign?.triggerType, dispatch]);

  const mergeFieldOptions = useMemo<MergeFieldOption[]>(() => {
    const mergedContactFields = [
      ...COMMON_FIELDS,
      ...(attributes?.attributes || []),
    ].filter(
      (field, index, allFields) =>
        !!field?.name &&
        allFields.findIndex(
          (candidate: any) => candidate?.name === field?.name
        ) === index
    );

    const contactOptions: MergeFieldOption[] =
      mergedContactFields.map((attr: any) => ({
        key: `contact-${attr?.name}`,
        label: `Contact: ${attr?.label || attr?.name}`,
        token: `contact.${attr?.name}`,
        category: 'contact' as const,
      })) ?? [];

    const eventOptions: MergeFieldOption[] =
      eventAttributes?.attributes
        ?.filter((attr: any) => !attr?.isDeleted)
        ?.map((attr: any) => ({
          key: `event-${attr?.name}`,
          label: `Event: ${attr?.name}`,
          token: `event.${attr?.name}`,
          category: 'event' as const,
        })) ?? [];

    const campaignOptions: MergeFieldOption[] = [
      {
        key: 'campaign-id',
        label: `Campaign ID${campaign?._id ? ` (${campaign._id})` : ''}`,
        token: 'campaign._id',
        category: 'campaign',
      },
      {
        key: 'campaign-name',
        label: `Campaign Name${campaign?.name ? ` (${campaign.name})` : ''}`,
        token: 'campaign.name',
        category: 'campaign',
      },
    ];

    return [...contactOptions, ...eventOptions, ...campaignOptions];
  }, [
    attributes?.attributes,
    campaign?._id,
    campaign?.name,
    eventAttributes?.attributes,
  ]);

  const updatePair = (
    list: KeyValueItem[],
    setList: React.Dispatch<React.SetStateAction<KeyValueItem[]>>,
    index: number,
    field: keyof KeyValueItem,
    value: string
  ) => {
    setList(
      list.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  };

  const updateHeaderPair = (
    list: HeaderItem[],
    setList: React.Dispatch<React.SetStateAction<HeaderItem[]>>,
    index: number,
    field: keyof HeaderItem,
    value: string
  ) => {
    setList(
      list.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addHeaderPair = (
    setList: React.Dispatch<React.SetStateAction<HeaderItem[]>>
  ) => {
    setList((prev) => [...prev, { name: '', value: '' }]);
  };

  const addPair = (
    setList: React.Dispatch<React.SetStateAction<KeyValueItem[]>>
  ) => {
    setList((prev) => [...prev, { key: '', value: '' }]);
  };

  const removeHeaderPair = (
    list: HeaderItem[],
    setList: React.Dispatch<React.SetStateAction<HeaderItem[]>>,
    index: number
  ) => {
    if (list.length === 1) {
      setList([{ name: '', value: '' }]);
      return;
    }
    setList(list.filter((_, itemIndex) => itemIndex !== index));
  };

  const removePair = (
    list: KeyValueItem[],
    setList: React.Dispatch<React.SetStateAction<KeyValueItem[]>>,
    index: number
  ) => {
    if (list.length === 1) {
      setList([{ key: '', value: '' }]);
      return;
    }
    setList(list.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSave = () => {
    setUrlTouched(true);
    if (!urlIsValid) {
      return;
    }

    const cleanedHeaders = headers.filter(
      (item) => item.name.trim() || item.value.trim()
    );
    const shouldClearHeaders =
      cleanedHeaders.length === 0 ||
      (cleanedHeaders.length === 1 &&
        isDefaultContentTypeHeader(cleanedHeaders[0]));

    const cleanedPayload = payload
      .filter((item) => item.key.trim() || item.value.trim())
      .reduce<PayloadObject>((acc, item) => {
        const key = item.key.trim();
        if (!key) return acc;
        acc[key] = item.value;
        return acc;
      }, {});

    onSave({
      webhookUrl: webhookUrl.trim(),
      method,
      headers: shouldClearHeaders ? [] : cleanedHeaders,
      payload: cleanedPayload,
    });
  };

  return (
    <div className="w-full flex">
      <div className="flex flex-col bg-card rounded-lg shadow-sm border border-border w-[30vw] h-[calc(75vh-100px)] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Link2 className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                Webhook
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Send an HTTP request in this step
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
          <div className="flex-1 p-4 space-y-5 overflow-y-auto">
            <div className="space-y-2">
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Webhook URL
              </label>
              <MergeFieldInput
                value={webhookUrl}
                onChange={setWebhookUrl}
                onBlur={() => setUrlTouched(true)}
                placeholder="https://example.com/webhook"
                className={`w-full px-3 py-2 rounded-lg border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:border-primary ${showUrlError ? 'border-destructive' : 'border-input'}`}
                options={mergeFieldOptions}
              />
              {showUrlError && (
                <p className="text-xs text-destructive">
                  {!trimmedWebhookUrl
                    ? 'Webhook URL is required.'
                    : 'Please enter a valid URL starting with https://'}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                URL Method
              </label>
              <Select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                size="md"
                options={[
                  { value: 'GET', label: 'GET' },
                  { value: 'POST', label: 'POST' },
                  { value: 'PUT', label: 'PUT' },
                  { value: 'PATCH', label: 'PATCH' },
                  { value: 'DELETE', label: 'DELETE' },
                ]}
                className="w-full text-xs"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Headers
                </label>
                <Button
                  variant="outline"
                  onClick={() => addHeaderPair(setHeaders)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {headers.map((item, index) => (
                  <div
                    key={`header-${index}`}
                    className="flex items-center gap-2"
                  >
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) =>
                        updateHeaderPair(
                          headers,
                          setHeaders,
                          index,
                          'name',
                          e.target.value
                        )
                      }
                      placeholder="content-type"
                      className="w-1/2 px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:border-primary"
                    />
                    <MergeFieldInput
                      value={item.value}
                      onChange={(nextValue) =>
                        updateHeaderPair(
                          headers,
                          setHeaders,
                          index,
                          'value',
                          nextValue
                        )
                      }
                      placeholder="application/json"
                      className="w-1/2 px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:border-primary"
                      options={mergeFieldOptions}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        removeHeaderPair(headers, setHeaders, index)
                      }
                      className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
                      aria-label="Remove header"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {supportsPayload && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Payload
                  </label>
                  <Button
                    variant="outline"
                    onClick={() => addPair(setPayload)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {payload.map((item, index) => (
                    <div
                      key={`payload-${index}`}
                      className="flex items-center gap-2"
                    >
                      <input
                        type="text"
                        value={item.key}
                        onChange={(e) =>
                          updatePair(
                            payload,
                            setPayload,
                            index,
                            'key',
                            e.target.value
                          )
                        }
                        placeholder="Key"
                        className="w-1/2 px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:border-primary"
                      />
                      <MergeFieldInput
                        value={item.value}
                        onChange={(nextValue) =>
                          updatePair(
                            payload,
                            setPayload,
                            index,
                            'value',
                            nextValue
                          )
                        }
                        placeholder="Value"
                        className="w-1/2 px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:border-primary"
                        options={mergeFieldOptions}
                      />
                      <button
                        type="button"
                        onClick={() => removePair(payload, setPayload, index)}
                        className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
                        aria-label="Remove payload row"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-border shrink-0 space-y-3">
            <Button
              variant="primary"
              disabled={!hasChanges || !urlIsValid}
              onClick={handleSave}
              className="w-full"
            >
              Save and build workflow
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full border-border text-foreground hover:bg-muted"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookPanel;
